import type { Due, DueKind } from '@/entities/shared/due/due';
import { dueState } from '@/entities/shared/due/due-state';
import { REMIND_DAYS } from '@/entities/shared/due/remind-days';
import { addMonths } from '@/shared/lib/dates/add-months';
import { daysBetween } from '@/shared/lib/dates/days-between';
import type { IsoDate } from '@/shared/lib/dates/iso-date';
import { today } from '@/shared/lib/dates/today';
import type { Item } from '../schemas/item';

type Candidate = {
  readonly date: IsoDate;
  readonly kind: DueKind;
};

const WINDOW: Readonly<Record<string, number>> = {
  expires: REMIND_DAYS.expires,
  shelf_life: REMIND_DAYS.expires,
  best_before: REMIND_DAYS.best_before,
  replace: REMIND_DAYS.replace,
};

/**
 * The earliest of use-by, best-before, replacement date and end of shelf life (counted from the
 * purchase). Best-before is quality, not safety: once past it asks to consume or replace (soon),
 * never overdue.
 */
export function nextDue(item: Item, on: IsoDate = today()): Due | null {
  const candidates: Candidate[] = [];
  if (item.expires_on) candidates.push({ date: item.expires_on, kind: 'expires' });
  if (item.best_before) candidates.push({ date: item.best_before, kind: 'best_before' });
  if (item.replace_on) candidates.push({ date: item.replace_on, kind: 'replace' });
  if (item.shelf_life_months && item.purchased_on) {
    candidates.push({ date: addMonths(item.purchased_on, item.shelf_life_months), kind: 'shelf_life' });
  }
  const [first] = candidates.sort((a, b) => a.date.localeCompare(b.date));
  if (!first) return null;
  const days = daysBetween(on, first.date);
  const urgency = dueState(days, WINDOW[first.kind] ?? 0);
  return {
    ...first,
    days,
    urgency: first.kind === 'best_before' && urgency === 'overdue' ? 'soon' : urgency,
  };
}
